export const PBB_PROFILE_EDIT_UNLINK_NARROWING_FLAG = 'fintech.platform.pbb-profile-edit-unlink-narrowing.rollout';

export const PBB_IDENTITY_FIELDS = ['firstName', 'lastName', 'phone', 'birthday', 'email'] as const;
export const NARROWED_PBB_UNENROLL_FIELDS = ['firstName', 'lastName'] as const;

export type PbbIdentityField = typeof PBB_IDENTITY_FIELDS[number];
type PbbIdentityValues = Partial<Record<PbbIdentityField, string | null | undefined>>;

const normalizeWhitespace = (value: string | null | undefined): string => (value ?? '').trim().replace(/\s+/g, ' ');

const normalizeName = (value: string | null | undefined): string => normalizeWhitespace(value).toLowerCase();

const normalizeEmail = (value: string | null | undefined): string => normalizeWhitespace(value).toLowerCase();

export const normalizePbbPhone = (value: string | null | undefined): string => {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

const normalizeBirthday = (value: string | null | undefined): string => {
  const trimmed = normalizeWhitespace(value);
  if (!trimmed) {
    return '';
  }

  const yearFirst = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(trimmed);
  const monthFirst = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  const match = yearFirst ?? monthFirst;
  if (!match) {
    return trimmed;
  }

  const [year, month, day] = yearFirst ? match.slice(1) : [match[3], match[1], match[2]];
  const normalized = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  const parsed = new Date(`${normalized}T00:00:00.000Z`);

  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(normalized) ? normalized : trimmed;
};

const NORMALIZERS: Record<PbbIdentityField, (value: string | null | undefined) => string> = {
  firstName: normalizeName,
  lastName: normalizeName,
  phone: normalizePbbPhone,
  birthday: normalizeBirthday,
  email: normalizeEmail,
};

export function getPbbIdentityChangedFields(
  submittedValues: PbbIdentityValues,
  storedValues: PbbIdentityValues
): PbbIdentityField[] {
  return PBB_IDENTITY_FIELDS.filter(
    (field) =>
      Object.prototype.hasOwnProperty.call(submittedValues, field) &&
      submittedValues[field] !== undefined &&
      NORMALIZERS[field](submittedValues[field]) !== NORMALIZERS[field](storedValues[field])
  );
}

export function isPbbUnlinkNarrowingEnabled(flags: Record<string, unknown>): boolean {
  return flags[PBB_PROFILE_EDIT_UNLINK_NARROWING_FLAG] === true;
}

export function shouldUnenrollForPbbIdentityChanges(
  changedFields: PbbIdentityField[],
  narrowedFieldSetEnabled: boolean
): boolean {
  if (!narrowedFieldSetEnabled) {
    return changedFields.length > 0;
  }

  return changedFields.some((field) => NARROWED_PBB_UNENROLL_FIELDS.some((narrowedField) => narrowedField === field));
}
